import { isColdPosition } from '@m1/geo-utils';
import useMountEffect from '@restart/hooks/useMountEffect';
import { useMemo } from 'react';
import { useDispatch, useStore } from 'react-redux';

import actions from '@/actions';
import { MAVLINK_MESSAGE_IDS as MESSAGE } from '@/define/messageId';
import EventEmitter from '@/libs/EventEmitter';
import telemetryStore from '@/stores/telemetryStore';
import { convertFloat32ToInt32 } from '@/utils/BinaryUtils';

const useFCTelemetryHandler = (robot, allowedMsgIds) => {
  const dispatch = useDispatch();
  const store = useStore();

  const isPX4 = useMemo(() => {
    return robot.model.maker.includes('PX4');
  }, []);

  useMountEffect(() => {
    const subscribeToken = EventEmitter.subscribe(`${robot.id}/telemetry/mavlink`, clarifyMessage);

    return () => {
      EventEmitter.unsubscribe(subscribeToken);
    };
  });

  const clarifyMessage = ({ header, payload }) => {
    const msgId = header.msgid;
    const sysId = header.sysid;
    const compId = header.compid;

    // CompId가 1이 아닌 경우 처리 제외
    if (compId !== 1) return;

    // 허용된 MsgId 아닌 경우 처리 제외
    if (allowedMsgIds && !allowedMsgIds.includes(msgId)) return;

    // SysId, CompId 저장
    dispatch(actions.address.setFc(robot.id, { sysId, compId }));

    switch (msgId) {
      case MESSAGE.HEARTBEAT:
        storeTelemetry(msgId, {
          isArm: payload.baseMode >= 128,
          mode: payload.customMode,
          status: payload.systemStatus,
          receivedAt: Date.now(), // shallowEqual 비교 통과시켜 알림 스킵 방지 (telemetryStore.js)
        });
        break;

      case MESSAGE.PARAM_VALUE:
        if (payload.paramId === '_HASH_CHECK') return;

        publishTelemetry(msgId, payload);
        updateRobotParams(payload);
        break;

      case MESSAGE.GPS_RAW_INT:
      case MESSAGE.ATTITUDE:
      case MESSAGE.VFR_HUD:
      case MESSAGE.AUTOPILOT_VERSION:
      case MESSAGE.EKF_STATUS_REPORT:
      case MESSAGE.VIBRATION:
        storeTelemetry(msgId, payload);
        break;

      case MESSAGE.GLOBAL_POSITION_INT:
        {
          const lat = payload.lat / 1e7;
          const lng = payload.lon / 1e7;

          // GPS 데이터 유효하지 않은 경우
          if (isColdPosition(lat, lng)) return;

          storeTelemetry(msgId, {
            lat,
            lng,
            alt: payload.alt / 1000,
            ralt: payload.relativeAlt / 1000,
            hdg: payload.hdg / 100,
          });

          EventEmitter.publish('position/update', {
            robotId: robot.id,
            position: { lat, lng },
          });
        }
        break;

      case MESSAGE.MISSION_CURRENT:
        {
          const data = { ...payload };
          // TODO: PX4 조건 상수화
          if (robot.model.maker === 'PX4') {
            data.seq++;
          }
          storeTelemetry(msgId, data);
        }
        break;

      case MESSAGE.MISSION_REQUEST:
      case MESSAGE.MISSION_COUNT:
      case MESSAGE.MISSION_ACK:
      case MESSAGE.MISSION_REQUEST_INT:
      case MESSAGE.MISSION_ITEM_INT:
      case MESSAGE.COMMAND_LONG:
      case MESSAGE.COMMAND_ACK:
      case MESSAGE.MAG_CAL_PROGRESS:
      case MESSAGE.MAG_CAL_REPORT:
        publishTelemetry(msgId, payload);
        break;

      case MESSAGE.BATTERY_STATUS:
        storeTelemetry(msgId, payload);
        break;

      case MESSAGE.CAMERA_FEEDBACK:
        storeTelemetry(msgId, {
          imgIdx: payload.imgIdx,
          lat: payload.lat / 1e7,
          lng: payload.lng / 1e7,
        });
        break;

      case MESSAGE.HOME_POSITION:
        {
          const lat = payload.latitude / 1e7;
          const lng = payload.longitude / 1e7;
          const homePosition = telemetryStore.get(robot.id, MESSAGE.HOME_POSITION);

          // 변경 감지된 경우에 한해 업데이트
          if (!homePosition || homePosition.lat !== lat || homePosition.lng !== lng) {
            storeTelemetry(msgId, { lat, lng });
          }
        }
        break;

      case MESSAGE.EXTENDED_SYS_STATE:
        storeTelemetry(msgId, {
          isFixedWing: [1, 4].includes(payload.vtolState),
          isMulticopter: [2, 3].includes(payload.vtolState),
          landedState: payload.landedState,
        });
        break;

      case MESSAGE.STATUSTEXT:
        if (payload) {
          dispatch(actions.notifications.add(robot.id, payload.text));
          publishTelemetry(msgId, payload);
          EventEmitter.publish('notifications/add');
        }
        break;

      default:
        break;
    }
  };

  const updateRobotParams = (payload) => {
    const params = { ...payload };

    // 6: 32-bit signed integer
    if (payload.paramType === 6 && isPX4) {
      // int32 변환
      params.paramValue = convertFloat32ToInt32(payload.paramValue);
    }
    // 9: 32-bit floating-point
    else if (payload.paramType === 9) {
      // JavaScript 부동소수점 연산 오차 방지 위한 4자리 제한
      params.paramValue = Number(payload.paramValue.toFixed(4));
    }

    dispatch(actions.robotParams.set(robot.id, params));
  };

  const storeTelemetry = (msgId, data) => {
    telemetryStore.set(robot.id, msgId, data);
  };

  const publishTelemetry = (msgId, data) => {
    EventEmitter.publish(`${robot.id}/telemetry/${msgId}`, data);
  };
};

export default useFCTelemetryHandler;
